import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * The "Top 10 Secrets to Succeeding with AI" e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/top-10-secrets-to-succeeding-with-ai/`), sent as five
 * screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the night-city artwork beside it, the grey "Why Download
 * This Ebook?" card with the cover pair and three bullets, the "What You'll
 * Learn" card with the stacked copies and five bullets, the "Join Our Community"
 * band, and the centred "Don't Miss Out!" closing ask. The copy is that page's
 * copy, verbatim.
 *
 * Where this page departs from the reference, and why — the same three
 * departures the sibling e-book pages carry, plus the two asked for in the
 * message:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "TRANSFORM YOUR BUSINESS TODAY" label.
 *    Every other hero here is that shape.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form under "Don't Miss Out!"; no form
 *    on this site submits anywhere yet, so reproducing it would mean the page's
 *    closing call to action silently drops every lead. The `check` below says
 *    so, as the sibling pages' do — so that band is the reference's heading and
 *    paragraph over a Download button.
 *  * **"Join Our Community" asks for contact, not a subscription.** Asked for in
 *    the message — *"subscribe butonu yerine de contact us olması yeterli"* —
 *    and it is the only honest button here for the same reason as above: there
 *    is no newsletter sign-up on this site that goes anywhere. The heading and
 *    the paragraph are the reference's; only the button changed.
 *  * **No testimonial and nothing after the closing ask**, because the message
 *    asked for the bottom band to go (*"en alttaki … kısmı olmasın"*). The
 *    sibling e-book pages end on a Hall of Fame card; this one deliberately does
 *    not.
 *
 * The images are the three the message attached, and they are the largest copies
 * of them anybody has sent — the 768px cover artwork and the two 900px mockups,
 * which is what the hero `check` is about.
 */
export const route: RouteDef = {
  id: 'ebookTop10Secrets',
  path: 'impact-labs/ebooks/top-10-secrets-to-succeeding-with-ai',
  priority: 0.7,
}

/**
 * The file itself, in the `website-documents` bucket — a local constant rather
 * than a key in `content/pages/links.ts`, which is only for CTAs shared between
 * pages. The object key is `content/ebooks.ts`' own, with its spaces
 * percent-encoded.
 */
export const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
    + 'fa77127f-4375-4a8d-9127-60f2e66ea6b8-Top%2010%20Secrets%20to%20Succeeding%20with%20AI.pdf'

export const ebookTop10Secrets: WrittenPage = {
  navLabel: 'Top 10 Secrets to Succeeding with AI',
  title: 'Top 10 Secrets to Succeeding with AI',
  description:
    'Actionable insights, real-world case studies and proven strategies for integrating AI into your operations. Download the Top 10 Secrets to Succeeding with AI e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Transform Your Business Today',
      headline: 'Top 10 Secrets to Succeeding with AI',
      body: [
        'Discover how to revolutionize your business with the power of Artificial Intelligence. '
          + 'Our comprehensive ebook provides actionable insights, real-world case studies, and '
          + 'proven strategies to help you integrate AI into your operations and drive growth.',
      ],
      ctas: [{ label: 'Download', href: PDF }],
      photo: {
        src: '/photo/top-10-secrets-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a night-time city avenue between lit towers, with the '
          + 'headlights of the traffic drawn out into long streaks of light.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the tops off the towers.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. It is generated art '
        + 'rather than a photograph, so a bigger export should exist wherever it was '
        + 'made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/top-10-secrets-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download This Ebook?',
      points: [
        'Practical Insights: Learn how to leverage AI to personalize customer experiences, '
          + 'optimize operations, and enhance marketing efforts.',
        'Real-World Examples: Discover how leading companies like Amazon, Coca-Cola, and Google '
          + 'are successfully using AI to stay ahead.',
        'Actionable Strategies: Get step-by-step guidance on building AI capabilities, ensuring '
          + 'strong governance, and driving strategic decisions with AI data.',
      ],
      ctas: [{ label: 'Download the eBook', href: PDF }],
      photo: {
        src: '/photo/top-10-secrets-ebook-covers.webp',
        alt:
          'Two copies of the Top 10 Secrets e-book, one behind the other, on a pale gradient '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      points: [
        'Personalizing Customer Experiences: Enhance customer loyalty and satisfaction with '
          + 'AI-driven personalization.',
        'Optimizing Operations: Streamline processes and reduce costs with AI-powered automation '
          + 'and predictive maintenance.',
        'Boosting Sales: Use predictive analytics to anticipate customer needs and optimize sales '
          + 'strategies.',
        'Innovating Product Development: Accelerate product innovation and reduce time-to-market '
          + 'with AI insights.',
        'Driving Strategic Decisions: Make informed, data-driven decisions to stay ahead in a '
          + 'competitive market.',
      ],
      ctas: [{ label: 'Download the eBook', href: PDF }],
      photo: {
        src: '/photo/top-10-secrets-ebook-stack.webp',
        alt: 'A stack of printed copies of the Top 10 Secrets e-book seen from above.',
      },
    },
    {
      kind: 'prose',
      heading: 'Join Our Community',
      sub:
        'Stay updated with the latest AI trends and insights. Get in touch for exclusive content '
        + 'and updates.',
      // The reference's Subscribe button, replaced on request — and there is no
      // newsletter sign-up on this site for it to have pointed at.
      cta: { label: 'Contact Us', href: '#contact' },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Don’t Miss Out!',
      sub:
        'Download our free ebook today and start your journey towards AI success. Transform your '
        + 'business with the secrets that industry leaders are using to achieve remarkable '
        + 'results.',
      cta: { label: 'Download the eBook', href: PDF },
    },
  ],
}
